import { Box, Checkbox, TextField } from "@material-ui/core"
import type { AutocompleteRenderInputParams, AutocompleteRenderOptionState, FilterOptionsState } from "@material-ui/lab"
import { Autocomplete, createFilterOptions } from "@material-ui/lab"
import { useCallback, useEffect, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"
import { connect } from "react-redux"
import { bosatlasFetchBundleList, bundleSearch } from "../../../redux"
import type { AppState, AppThunkDispatch, BosatlasActions, Bundle } from "../../../redux"
import { createNewBundle } from "../../../services/BundleService"
import { Body2 } from "../../atoms"

import "./bundle-selector.styles.scss"
import CreateNewFolder from "@material-ui/icons/CreateNewFolder"
import { useTranslation } from "react-i18next"

export interface IBundleSelectorProps {
  selectedBundles: Bundle[]
  onChange: (bundles: Bundle[]) => void
}

export interface IBundleSelectorStateProps {
  bundles?: Bundle[]
  fetchBundles: (searchValue: string) => void
  loadBundles: () => void
}

function BundleSelectorView(props: IBundleSelectorProps & IBundleSelectorStateProps): ReactElement {
  const { bundles, selectedBundles, onChange, fetchBundles, loadBundles } = props

  const { t } = useTranslation("folder")

  const [searchValue, setSearchValue] = useState<string>("")
  const [createdBundleId, setCreatedBundleId] = useState<string>()

  const filter = createFilterOptions<Bundle>({
    stringify: (option) => option.title
  })

  const isBundleSelected: (bundle: Bundle) => boolean = useCallback(
    (bundle: Bundle) => {
      return selectedBundles.some((b: Bundle) => b.id === bundle.id)
    },
    [selectedBundles]
  )
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchBundles(searchValue)
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (!createdBundleId || !bundles) return

    const createdBundle: Bundle | undefined = bundles.find((bundle: Bundle) => bundle.id === createdBundleId)

    if (createdBundle) {
      setCreatedBundleId(undefined)
      onChange([...selectedBundles, createdBundle])
    }
  }, [createdBundleId, bundles])

  function onSearchChanged(event: ChangeEvent<HTMLInputElement>): void {
    setSearchValue(event.target.value)
    fetchBundles(event.target.value)
  }

  function onBundleChange(_: ChangeEvent<{}>, value: Bundle[]): void {
    const hasUnsavedBundle: boolean = value.some((bundle: Bundle) => !bundle.id)

    setSearchValue("")
    fetchBundles("")

    if (hasUnsavedBundle) {
      addAndSelectBundle(searchValue)
    } else {
      processBundleClicked(value)
    }
  }

  function processBundleClicked(bundles: Bundle[]): void {
    if (bundles.length <= 1) {
      onChange(bundles)
      return
    }

    const lastBundle: Bundle = { ...bundles[bundles.length - 1] }
    const lastIsDuplicate: boolean = bundles.filter((bundle: Bundle) => bundle.id === lastBundle.id).length > 1
    if (!lastIsDuplicate) {
      onChange(bundles)
      return
    }
    const newBundles: Bundle[] = bundles.filter((bundle: Bundle) => bundle.id !== lastBundle.id)
    onChange(newBundles)
  }

  function getRenderOptions(option: Bundle, _: AutocompleteRenderOptionState): ReactElement {
    const notCreated: boolean = !("id" in option)

    return (
      <Box className={`bundle-selector-popper-container ${notCreated ? "not-created" : ""}`}>
        {notCreated && <CreateNewFolder />}
        {!notCreated && <Checkbox color="primary" checked={isBundleSelected(option)} />}
        <Body2>{option.title}</Body2>
      </Box>
    )
  }

  function getFilterOptions(options: Bundle[], params: FilterOptionsState<Bundle>): Bundle[] {
    const filtered = filter(options, params) as Bundle[]

    if (params.inputValue !== "" && filtered.findIndex((x) => x.title === params.inputValue) === -1) {
      filtered.push({
        title: t("createSpecificFolder", {
          folderName: params.inputValue
        })
      })
    }

    return filtered
  }

  async function addAndSelectBundle(title: string): Promise<void> {
    const bundleId: string = await createNewBundle(title)
    setCreatedBundleId(bundleId)
    fetchBundles("")
    loadBundles()
  }

  return (
    <Autocomplete
      options={bundles ? bundles : []}
      getOptionLabel={(option: Bundle) => option.title}
      onChange={onBundleChange}
      multiple
      disableCloseOnSelect
      value={selectedBundles}
      classes={{
        option: "bundle-selector-popper-item",
        listbox: "bundle-selector-popper-listbox"
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          variant="outlined"
          value={searchValue}
          onChange={onSearchChanged}
          placeholder={t("chooseOrCreate")}
        />
      )}
      renderOption={getRenderOptions}
      filterOptions={getFilterOptions}
    />
  )
}

function mapStateToProps(state: AppState) {
  return {
    bundles: state.bundle.bundleList
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasActions>) {
  return {
    fetchBundles: (searchValue: string) => dispatch(bundleSearch(searchValue)),
    loadBundles: () => dispatch(bosatlasFetchBundleList())
  }
}

export const BundleSelector = connect(mapStateToProps, mapDispatchToProps)(BundleSelectorView)
